(() => {
  const sanitizeName = (raw) => {
    const value = String(raw || '').toUpperCase().replace(/[^A-Z0-9 ]/g, '').replace(/ {2,}/g, ' ').replace(/^ +/g, '');
    return value.slice(0, 12);
  };

  const validName = (value) => {
    const normalized = String(value || '').trim();
    return normalized.length > 0 && normalized.length <= 12 && /^[A-Z0-9]+(?: [A-Z0-9]+)*$/.test(normalized);
  };

  window.OROCUFFFiveVerse = Object.freeze({ sanitizeName, validName });

  const getParts = (purchase) => ({
    badgePicker: purchase?.querySelector('[data-oc-five-badge-picker]'),
    personalization: purchase?.querySelector('[data-oc-five-personalization]')
  });

  const getState = (purchase) => {
    const initialBadgeKey = getParts(purchase).badgePicker?.dataset.initialBadgeKey || 'original';
    if (!purchase.ocFiveState) purchase.ocFiveState = { badgeKey: initialBadgeKey, personalized: false, name: '' };
    return purchase.ocFiveState;
  };

  const activateGalleryMedia = (purchase, mediaId) => {
    if (!mediaId) return;
    const { badgePicker } = getParts(purchase);
    const sectionId = badgePicker?.closest('variant-radios,variant-selects')?.dataset.section;
    const gallery = (sectionId && document.getElementById(`MediaGallery-${sectionId}`)) || document.querySelector('media-gallery');
    if (!gallery) return;
    const hasClassicDisclosure = Boolean(purchase?.querySelector('[data-oc-five-badge-toggle]'));
    if (typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(mediaId, false, hasClassicDisclosure);
      if (!hasClassicDisclosure) return;
    }
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const activeMedia = viewer?.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
    if (!activeMedia) return;
    viewer.querySelectorAll('[data-media-id]').forEach((item) => item.classList.remove('is-active'));
    activeMedia.classList.add('is-active');
    activeMedia.parentElement?.scrollTo({
      left: activeMedia.offsetLeft,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
    const thumbnail = gallery.querySelector(`[data-target="${CSS.escape(mediaId)}"]`);
    gallery.querySelectorAll('[data-target] button').forEach((button) => button.removeAttribute('aria-current'));
    thumbnail?.querySelector('button')?.setAttribute('aria-current', 'true');
    thumbnail?.parentElement?.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
  };

  const syncBadge = (purchase, switchMedia = true, emit = true) => {
    const { badgePicker } = getParts(purchase);
    if (!badgePicker) return;
    const selected = badgePicker.querySelector('[data-oc-five-badge][aria-checked="true"]') || badgePicker.querySelector('[data-oc-five-badge]');
    if (!selected) return;
    const badgeStyle = badgePicker.querySelector('[data-oc-five-badge-style]');
    const metal = badgePicker.querySelector('[data-oc-five-metal]');
    const internalId = badgePicker.querySelector('[data-oc-five-internal-id]');
    getState(purchase).badgeKey = selected.value || 'original';
    badgeStyle.value = selected.dataset.style || '';
    metal.value = selected.dataset.metal || '';
    internalId.value = selected.dataset.internalId || '';
    internalId.disabled = !internalId.value;
    if (switchMedia) activateGalleryMedia(purchase, selected.dataset.mediaId);
    if (emit) purchase.dispatchEvent(new CustomEvent('ocFiveBadgeChanged'));
  };

  const syncBadgeDisclosure = (purchase) => {
    const { badgePicker } = getParts(purchase);
    const toggle = badgePicker?.querySelector('[data-oc-five-badge-toggle]');
    if (!badgePicker || !toggle) return;
    const selected = badgePicker.querySelector('[data-oc-five-badge][aria-checked="true"]');
    const cards = Array.from(badgePicker.querySelectorAll('[data-oc-five-badge]'));
    if (selected && cards.indexOf(selected) >= 4) badgePicker.dataset.expanded = 'true';
    const expanded = badgePicker.dataset.expanded === 'true';
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.textContent = expanded ? 'Show Fewer Designs' : `View All ${cards.length} Designs`;
  };

  const syncPersonalization = (purchase, emit = true) => {
    const { personalization } = getParts(purchase);
    if (!personalization) return;
    const state = getState(purchase);
    const enabled = personalization.querySelector('[data-oc-five-personalization-mode][aria-checked="true"]')?.value === 'yes';
    const nameFields = personalization.querySelector('[data-oc-five-name-fields]');
    const nameInput = personalization.querySelector('[data-oc-five-name]');
    const personalProperty = personalization.querySelector('[data-oc-five-personalization-property]');
    const nameProperty = personalization.querySelector('[data-oc-five-name-property]');
    state.personalized = enabled;
    nameFields.hidden = !enabled;
    nameInput.disabled = !enabled;
    nameProperty.disabled = !enabled;
    personalProperty.value = enabled ? 'Add Name Embossing (+$7.99)' : 'No Personalization';
    if (enabled) {
      nameInput.value = state.name;
      nameProperty.value = state.name;
      activateGalleryMedia(purchase, personalization.dataset.personalizationMediaId);
    } else {
      state.name = '';
      nameInput.value = '';
      nameProperty.value = '';
      nameInput.setCustomValidity('');
    }
    if (emit) personalization.dispatchEvent(new CustomEvent('ocFivePersonalizationChanged', { bubbles: true }));
  };

  const syncName = (purchase, input) => {
    const clean = sanitizeName(input.value);
    if (input.value !== clean) input.value = clean;
    const state = getState(purchase);
    state.name = clean.trim();
    const nameProperty = purchase.querySelector('[data-oc-five-name-property]');
    if (nameProperty) nameProperty.value = state.name;
    input.setCustomValidity(clean && !validName(clean) ? 'Use 1–12 characters with A–Z, 0–9 and single spaces only.' : '');
  };

  const createController = (purchase) => {
    if (purchase.ocFiveVerse) return;
    purchase.ocFiveVerse = {
      get badgePicker() { return getParts(purchase).badgePicker; },
      get personalization() { return getParts(purchase).personalization; },
      get serviceVariantId() { return getParts(purchase).personalization?.dataset.serviceVariantId || ''; },
      get serviceAvailable() { return getParts(purchase).personalization?.dataset.serviceAvailable === 'true'; },
      selectedBadge() {
        const selected = getParts(purchase).badgePicker?.querySelector('[data-oc-five-badge][aria-checked="true"]');
        return {
          key: selected?.value || 'original',
          style: selected?.dataset.style || 'Scripture Cross',
          metal: selected?.dataset.metal || 'Antique Silver',
          internalId: selected?.dataset.internalId || '',
          variantSuffix: selected?.dataset.variantSuffix || ''
        };
      },
      personalized() { return getState(purchase).personalized; },
      name() { return getState(purchase).name; },
      setMultiMode(active) {
        const { badgePicker, personalization } = getParts(purchase);
        if (!badgePicker || !personalization) return;
        badgePicker.dataset.multiHidden = String(active);
        personalization.dataset.multiHidden = String(active);
        badgePicker.querySelectorAll('[data-oc-five-badge]').forEach((button) => { button.disabled = active; });
        const internalId = badgePicker.querySelector('[data-oc-five-internal-id]');
        if (internalId) internalId.disabled = active || !internalId.value;
        personalization.querySelectorAll('[data-oc-five-personalization-mode]').forEach((button) => { button.disabled = active; });
        const nameInput = personalization.querySelector('[data-oc-five-name]');
        const nameProperty = personalization.querySelector('[data-oc-five-name-property]');
        const personalProperty = personalization.querySelector('[data-oc-five-personalization-property]');
        if (nameInput) nameInput.disabled = active || !this.personalized();
        if (nameProperty) nameProperty.disabled = active || !this.personalized();
        if (personalProperty) personalProperty.disabled = active;
      },
      validate() {
        if (!this.personalized()) return '';
        if (!this.serviceAvailable || !this.serviceVariantId) return 'Name embossing is not available until the $7.99 service is confirmed in Shopify.';
        if (!validName(this.name())) return 'Enter 1–12 characters using A–Z, 0–9 and single spaces.';
        return '';
      }
    };
  };

  const initializePurchase = (purchase, emit = false) => {
    const { badgePicker, personalization } = getParts(purchase);
    if (!badgePicker || !personalization) return;
    createController(purchase);
    const state = getState(purchase);
    const badge = badgePicker.querySelector(`[data-oc-five-badge][value="${CSS.escape(state.badgeKey)}"]`) || badgePicker.querySelector('[data-oc-five-badge]');
    badgePicker.querySelectorAll('[data-oc-five-badge]').forEach((button) => button.setAttribute('aria-checked', String(button === badge)));
    personalization.querySelectorAll('[data-oc-five-personalization-mode]').forEach((button) => button.setAttribute('aria-checked', String(button.value === (state.personalized ? 'yes' : 'no'))));
    const nameInput = personalization.querySelector('[data-oc-five-name]');
    if (nameInput) nameInput.value = state.name;
    syncBadge(purchase, state.badgeKey !== 'original', emit);
    syncBadgeDisclosure(purchase);
    syncPersonalization(purchase, emit);
  };

  const boot = (root = document, emit = false) => {
    const purchases = new Set();
    root.querySelectorAll?.('[data-oc-five-badge-picker]').forEach((picker) => {
      const purchase = picker.closest('.oc-purchase');
      if (purchase) purchases.add(purchase);
    });
    purchases.forEach((purchase) => initializePurchase(purchase, emit));
  };

  document.addEventListener('click', (event) => {
    const badgeToggle = event.target.closest?.('[data-oc-five-badge-toggle]');
    if (badgeToggle) {
      const purchase = badgeToggle.closest('.oc-purchase');
      const picker = badgeToggle.closest('[data-oc-five-badge-picker]');
      picker.dataset.expanded = String(picker.dataset.expanded !== 'true');
      syncBadgeDisclosure(purchase);
      return;
    }
    const badge = event.target.closest?.('[data-oc-five-badge]');
    if (badge && !badge.disabled) {
      const purchase = badge.closest('.oc-purchase');
      const picker = badge.closest('[data-oc-five-badge-picker]');
      picker.querySelectorAll('[data-oc-five-badge]').forEach((button) => button.setAttribute('aria-checked', String(button === badge)));
      syncBadge(purchase);
      return;
    }
    const mode = event.target.closest?.('[data-oc-five-personalization-mode]');
    if (mode && !mode.disabled) {
      const purchase = mode.closest('.oc-purchase');
      const personalization = mode.closest('[data-oc-five-personalization]');
      personalization.querySelectorAll('[data-oc-five-personalization-mode]').forEach((button) => button.setAttribute('aria-checked', String(button === mode)));
      syncPersonalization(purchase);
    }
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches?.('[data-oc-five-name]')) return;
    syncName(event.target.closest('.oc-purchase'), event.target);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches?.('[data-oc-five-name]')) event.stopPropagation();
  }, true);

  document.addEventListener('focusout', (event) => {
    if (!event.target.matches?.('[data-oc-five-name]')) return;
    event.target.value = event.target.value.trim();
    syncName(event.target.closest('.oc-purchase'), event.target);
  });

  boot();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('afterVariantChanged', () => boot(document, true));
})();
